import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@repo/ping-react-js";
import { Plus, ShieldCheck, Building2, UserRound } from "lucide-react";
import {
  fetchUserCreds,
  setUserCreds,
  deleteUserCreds,
  fetchCredentialSources,
  type CredEntry,
} from "../api/credentials";
import { fetchMe, type MeResponse } from "../api/me";
import CredentialEditModal from "../components/CredentialEditModal";
import CredentialCardGrid from "../components/CredentialCardGrid";
import "./CredentialsPage.scss";

type ModalState =
  | { type: "none" }
  | { type: "edit"; source: string; secretTypes: string[] }
  | { type: "add" };

const CredentialsPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [creds, setCreds] = useState<CredEntry[]>([]);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [targetUser, setTargetUser] = useState<{ id: number; username: string; email: string } | null>(null);
  const [isSelf, setIsSelf] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ type: "none" });
  const [sourceNameMap, setSourceNameMap] = useState<Record<string, string>>({});
  const [selfServeCredentialManagementEnabled, setSelfServeCredentialManagementEnabled] = useState(true);

  const load = useCallback(async () => {
    if (!accessToken || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const meResult = await fetchMe(accessToken);
      const [result, sourcesResult] = await Promise.all([
        fetchUserCreds(accessToken, userId),
        fetchCredentialSources(accessToken).catch(() => ({ sources: [] })),
      ]);
      setIsSelf(String(meResult.id) === userId);
      setTargetUser(result.user ?? { id: meResult.id, username: meResult.username, email: meResult.email });
      setCreds(result.credentials);
      setSelfServeCredentialManagementEnabled(meResult.self_serve_credential_management_enabled !== false);
      setMe(meResult);
      setSourceNameMap(
        Object.fromEntries(
          sourcesResult.sources.flatMap((s) => [[s.code, s.member_code ?? s.code], ...(s.member_code ? [[s.member_code, s.member_code]] : [])])),
      );
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Failed to load credentials",
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!loading && !selfServeCredentialManagementEnabled) {
      navigate("/", { replace: true });
    }
  }, [loading, selfServeCredentialManagementEnabled, navigate]);

  const handleSave = async (
    source: string,
    fields: Record<string, string>,
  ) => {
    if (!accessToken || !userId) return;
    setSaving(source);
    setSaveError(null);
    try {
      await setUserCreds(accessToken, userId, source, fields);
      await load();
      setModal({ type: "none" });
    } catch (err: unknown) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to save credentials",
      );
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (source: string) => {
    if (!accessToken || !userId) return;
    setSaving(source);
    setSaveError(null);
    try {
      await deleteUserCreds(accessToken, userId, source);
      await load();
    } catch (err: unknown) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to remove credentials",
      );
    } finally {
      setSaving(null);
    }
  };

  const configuredSources = creds.sort((a, b) => a.source.localeCompare(b.source));

  return (
    <>
      <header className="app-header">
        <div className="creds-header">
          <h1>Credentials</h1>
          <div className="creds-header-actions">
            {!isSelf && targetUser && (
              <div className="creds-user-org">
                <UserRound size={14} />
                <span>{targetUser.username}{targetUser.email && targetUser.email !== targetUser.username ? ` (${targetUser.email})` : ""}</span>
              </div>
            )}
            {isSelf && me?.organization && (
              <div className="creds-user-org">
                <Building2 size={14} />
                <span>{me.organization.name} ({me.organization.short_name})</span>
              </div>
            )}
            {selfServeCredentialManagementEnabled && (
              <button
                className="apikeys-create-btn"
                onClick={() => {
                  setSaveError(null);
                  setModal({ type: "add" });
                }}
              >
                <Plus size={16} />
                Add Integration
              </button>
            )}
          </div>
        </div>
      </header>
      <div className="app-content">
        {loading && <p className="creds-status">Loading...</p>}
        {error && <p className="creds-status creds-error">{error}</p>}

        {!loading && !error && (
          <>
            {configuredSources.length === 0 ? (
              <p className="creds-status">
                No credentials configured. Click "Add Integration" to get started.
              </p>
            ) : (
              <CredentialCardGrid
                entries={configuredSources}
                sourceNameMap={sourceNameMap}
                saving={saving}
                onEdit={(source, secretTypes) => {
                  setSaveError(null);
                  setModal({ type: "edit", source, secretTypes });
                }}
                onDelete={handleDelete}
              />
            )}

            <div className="creds-info">
              <ShieldCheck size={14} />
              <span>
                {isSelf
                  ? "Your credentials are used when accessing data sources."
                  : "These credentials are used when this user accesses data sources."}
              </span>
            </div>
          </>
        )}
      </div>

      {(modal.type === "edit" || modal.type === "add") && (
        <CredentialEditModal
          source={modal.type === "edit" ? modal.source : ""}
          saving={saving === (modal.type === "edit" ? modal.source : "__new__")}
          title={modal.type === "edit" ? `Credentials for ${modal.source}` : "Add Integration"}
          existingSources={configuredSources.map((c) => c.source)}
          secretTypes={modal.type === "edit" ? modal.secretTypes : []}
          saveError={saveError}
          onSave={(source, fields) => handleSave(source, fields)}
          onClose={() => setModal({ type: "none" })}
        />
      )}
    </>
  );
};

export default CredentialsPage;
